import { useMemo, useState } from "react";
import { useFormik } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  FormControl,
  Box,
} from "@mui/material";
import { AdminRegionResDto } from "service/api/estabrookSchemas";
import { DialogProps } from "@mui/material";
import {
  useAdminRegionV2ControllerTransferLocationsToRegion,
  useAdminRegionV2ControllerAddLocationsToRegion,
  useAdminLocationV2ControllerGetAllLocationsPaginated,
} from "service";
import {
  ControlledLabelSelectOption,
  ControlledSelect,
  ControlledSelectOption,
  makeSxStyles,
} from "ui";
import useRegionManagementStore from "../../../state/region-management-store";
import { useEffect } from "react";
import { toast } from "react-toastify";
import CustomStepper from "../route-planner/build/stepper";
import { MapMarker } from "../../../components/map/map.types";
import RegionSequenceModal from "./region-sequence-modal";
import { isUnassignedAddress } from "../../../shared/utils/address-assignment";
import { queryClient } from "../../../query-client";
import {
  createRegionsInvalidationPredicate,
  createLocationsInvalidationPredicate,
} from "service/api/estabrookQueryKeys";

export interface MoveLocationsDialogProps
  extends Omit<
    DialogProps,
    "onClose" | "open" | "style" | "inputValue" | "handler"
  > {
  onClose: () => void;
  onRefresh: () => void;
  open?: boolean;
  shouldSequence: boolean;
  defaultActiveStep?: number;
  regions: AdminRegionResDto[];
}

interface MoveRegionValues {
  targetRegionId: string;
  targetSeqNumber: number;
  transferMode: "before" | "after";
  locationIds: { locationId: string; seqNumber: number }[];
}

interface FieldOptions {
  key: keyof MoveRegionValues;
  overridenFormValue?: string | undefined;
  overrideFormValue?: boolean;
  label: string;
  placeholder: string;
  options: ControlledSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
}

const MoveLocationsDialog: React.FC<MoveLocationsDialogProps> = (props) => {
  const {
    onClose,
    onRefresh,
    open,
    shouldSequence,
    defaultActiveStep = 0,
    regions: allRegions,
    ...rest
  } = props;
  const {
    selectedAddresses,
    resetAllSelected,
    selectedAddressesForSequencing,
    setSelectedAddressesForSequencing,
    setMapCentering,
    setAllSequenced,
    allSequenced,
    setSidebar,
  } = useRegionManagementStore();
  const styles = useStyles();
  const [sequence, setSequence] = useState<string | undefined>("");
  const [activeStep, setActiveStep] = useState<number>(defaultActiveStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [moveRegionValues, setMoveRegionValues] = useState<MoveRegionValues>({
    targetRegionId: "",
    targetSeqNumber: 1,
    transferMode: "before",
    locationIds: [],
  });

  useEffect(() => {
    setActiveStep(defaultActiveStep);

    if (defaultActiveStep === 1) {
      setSidebar(true);
    } else {
      setSidebar(false);
    }

    return () => {
      setActiveStep(0);
      setSidebar(false);
    };
  }, [defaultActiveStep, setSidebar]);

  const handleClose = () => {
    setAllSequenced(false);
    // Keep the sidebar in sync with the selection. Cancelling the dialog must
    // not orphan the selection (markers still highlighted but no sidebar to
    // act on them). If addresses are still selected, the sidebar stays open.
    setSidebar(selectedAddresses.length > 0);
    setActiveStep(0);
    onClose();
  };

  const { values, setFieldValue, setFieldTouched, errors, touched, resetForm } =
    useFormik<MoveRegionValues>({
      initialValues: {
        targetRegionId: "",
        targetSeqNumber: 1,
        transferMode: "before",
        locationIds: [],
      },
      enableReinitialize: true,
      onSubmit: (values) => {
        submitForm(values);
      },
      validateOnBlur: false,
      validateOnChange: false,
    });

  const prepareAddressesForResequence = (futureTargetSeqNumber: number) => {
    const targetRegion = allRegions.find(
      (region) => region.id === values.targetRegionId,
    );

    if (!targetRegion) return;

    // Use the fetched target region addresses instead of the filtered store addresses
    const targetRegionAddressesRaw = targetRegionAddressesData?.data || [];

    // Transform to MapMarker format
    const targetRegionAddresses: MapMarker[] = targetRegionAddressesRaw.map(
      (location) => ({
        id: location.id,
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address,
        locationSequence:
          typeof location.locationSequence === "number"
            ? location.locationSequence
            : location.locationSequence === null ||
                location.locationSequence === undefined
              ? null
              : Number(location.locationSequence) || null,
        customer: location.customer,
        region: location.region
          ? {
              ...location.region,
              color:
                typeof location.region.color === "string"
                  ? location.region.color
                  : undefined,
            }
          : undefined,
        selected: false,
      }),
    );

    // Adjust sequences of existing addresses to make room for new ones
    const resequencedTargetRegionAddresses: MapMarker[] =
      targetRegionAddresses.map((address) => {
        if (
          address.locationSequence !== null &&
          address.locationSequence !== undefined &&
          address.locationSequence >= futureTargetSeqNumber
        ) {
          return {
            ...address,
            locationSequence:
              address.locationSequence + selectedAddresses.length,
          };
        }
        return address;
      });

    // Combine target region addresses with selected addresses (unsequenced)
    const regionWithLocationsToResequence: MapMarker[] = [
      ...resequencedTargetRegionAddresses,
      ...selectedAddresses.map((address) => ({
        ...address,
        locationSequence: null,
        region: {
          id: "unassigned",
          name: "Unassigned",
          color: "unassigned",
        },
      })),
    ];

    // Only sequence the newly added addresses
    const addressesToSequence = selectedAddresses.map((address) => ({
      ...address,
      locationSequence: null,
      selected: false,
      region: {
        id: "unassigned",
        name: "Unassigned",
        color: "unassigned",
      },
    }));

    setMapCentering(undefined, regionWithLocationsToResequence);
    setSelectedAddressesForSequencing(
      addressesToSequence,
      futureTargetSeqNumber,
    );

    setSidebar(true);
  };

  const regionOptions: ControlledLabelSelectOption[] = allRegions.map(
    (region) => ({
      value: region.id,
      label: region.name,
    }),
  );

  // Fetch addresses for the selected target region independently of the main filter
  const { data: targetRegionAddressesData } =
    useAdminLocationV2ControllerGetAllLocationsPaginated(
      {
        queryParams: {
          region__in: values.targetRegionId
            ? [values.targetRegionId]
            : undefined,
        },
      },
      {
        enabled: !!values.targetRegionId,
      },
    );

  const addressOptions: ControlledLabelSelectOption[] = useMemo(() => {
    if (!values.targetRegionId) return [];

    if (!targetRegionAddressesData?.data) return [];

    const targetRegionAddresses = targetRegionAddressesData.data;

    return targetRegionAddresses
      .sort((a, b) => {
        const seqA =
          typeof a.locationSequence === "number" ? a.locationSequence : 0;
        const seqB =
          typeof b.locationSequence === "number" ? b.locationSequence : 0;
        return seqA - seqB;
      })
      .map((addr) => ({
        value: addr.locationSequence?.toString() || "",
        label: `${addr.locationSequence ?? ""}. ${addr.address}`.trim(),
      }));
  }, [values.targetRegionId, targetRegionAddressesData]);

  // Determine if all selected addresses are unassigned
  const allSelectedAddressesUnassigned =
    selectedAddresses.every(isUnassignedAddress);

  const { mutateAsync: transferLocations } =
    useAdminRegionV2ControllerTransferLocationsToRegion({
      onSuccess: async (_, variables) => {
        // Get the target region name
        const targetRegion = allRegions.find(
          (region) => region.id === variables.body.targetRegionId,
        );
        const targetRegionName = targetRegion?.name || "target region";

        toast.success(
          `${selectedAddresses.length} ${selectedAddresses.length === 1 ? "address has" : "addresses have"} been moved to ${targetRegionName} successfully.`,
        );
        await queryClient.invalidateQueries(
          createRegionsInvalidationPredicate(),
        );
        await queryClient.invalidateQueries(
          createLocationsInvalidationPredicate(),
        );
        onRefresh();
        resetAllSelected();
      },
    });

  const { mutateAsync: addUnassignedLocations } =
    useAdminRegionV2ControllerAddLocationsToRegion({
      onSuccess: async (_, variables) => {
        // Get the target region name
        const targetRegion = allRegions.find(
          (region) => region.id === variables.pathParams.id,
        );
        const targetRegionName = targetRegion?.name || "target region";

        toast.success(
          `${selectedAddresses.length} ${selectedAddresses.length === 1 ? "address has" : "addresses have"} been added to ${targetRegionName} successfully.`,
        );
        await queryClient.invalidateQueries(
          createRegionsInvalidationPredicate(),
        );
        await queryClient.invalidateQueries(
          createLocationsInvalidationPredicate(),
        );
        onRefresh();
        resetAllSelected();
      },
    });

  useEffect(() => {
    setSequence("");
  }, [values.targetRegionId]);

  useEffect(() => {
    if (!open) {
      setSequence("");
      resetForm();
      // Restore the sidebar to match the current selection when the dialog
      // closes, so a remaining selection is still actionable.
      setSidebar(selectedAddresses.length > 0);
      setActiveStep(0);
    } else if (activeStep === 1) {
      setSidebar(true);
    }
  }, [open, resetForm, activeStep, setSidebar, selectedAddresses.length]);

  useEffect(() => {
    if (open) {
      setAllSequenced(false);
    }
  }, [open, setAllSequenced]);

  const fields: FieldOptions[] = [
    {
      key: "targetRegionId",
      label: `Move ${selectedAddresses.length} addresses to region`,
      placeholder: "Select Region",
      options: regionOptions,
      onChange: (value) => {
        setFieldValue("targetRegionId", value);
        setFieldTouched("targetRegionId", true);
      },
    },
    {
      key: "transferMode",
      overrideFormValue: undefined,
      label: "",
      placeholder: "Before",
      options: placementOptions,
      onChange: (value) => {
        setFieldValue("transferMode", value);
        setFieldTouched("transferMode", true);
      },
      disabled: !addressOptions.length,
    },
    {
      key: "targetSeqNumber",
      overridenFormValue: sequence,
      overrideFormValue: true,
      label: "Address",
      placeholder: "Select Address",
      options: addressOptions,
      onChange: (value) => {
        setSequence(value);
        setFieldTouched("targetSeqNumber", true);
      },
      disabled: !addressOptions.length,
    },
  ];

  const submitForm = async (values: MoveRegionValues) => {
    let modifiedValues = { ...values };
    setIsSubmitting(true);

    if (!allSequenced) {
      const sortedSelectedAddresses = selectedAddresses.sort(
        (a, b) => (a.locationSequence ?? 0) - (b.locationSequence ?? 0),
      );
      const selectedSeqNumber = Number(sequence) === 0 ? 1 : Number(sequence);
      // When "after" is selected, send the sequence as-is since backend handles the +1
      const targetSeqNumber = selectedSeqNumber;
      let futureTargetSeqNumber = targetSeqNumber;

      const locationIds = sortedSelectedAddresses.map((address) => ({
        locationId: address.id,
        seqNumber: futureTargetSeqNumber++,
      }));

      modifiedValues = {
        ...values,
        targetSeqNumber,
        locationIds,
        transferMode: values.transferMode,
      };

      setMoveRegionValues(modifiedValues);

      if (shouldSequence) {
        setActiveStep(activeStep + 1);
        // For sequencing modal, when "after" is selected, start from selectedSeqNumber + 1
        // so the displayed numbers are correct (e.g., after 50 should show 51, 52, 53)
        const sequencingStartNumber =
          values.transferMode === "after"
            ? selectedSeqNumber + 1
            : targetSeqNumber;
        prepareAddressesForResequence(sequencingStartNumber);
        setIsSubmitting(false);
      } else {
        // Close modal immediately before starting async operations
        onClose();
        setIsSubmitting(false);

        try {
          if (allSelectedAddressesUnassigned) {
            addUnassignedLocations({
              pathParams: {
                id: modifiedValues.targetRegionId,
              },
              body: {
                locations: modifiedValues.locationIds,
                targetSeqNumber: modifiedValues.targetSeqNumber,
                transferMode: modifiedValues.transferMode,
              },
            });
          } else {
            // Group addresses by source region
            const addressesByRegion = selectedAddresses.reduce(
              (acc, address) => {
                const regionId = address.region?.id;
                if (regionId) {
                  if (!acc[regionId]) {
                    acc[regionId] = [];
                  }
                  acc[regionId].push(address.id);
                }
                return acc;
              },
              {} as Record<string, string[]>,
            );

            // Transfer locations from each source region
            for (const [sourceRegionId, locationIds] of Object.entries(
              addressesByRegion,
            )) {
              transferLocations({
                pathParams: {
                  id: sourceRegionId,
                },
                body: {
                  targetRegionId: modifiedValues.targetRegionId,
                  targetSeqNumber: modifiedValues.targetSeqNumber,
                  transferMode: modifiedValues.transferMode,
                  locations: locationIds,
                },
              });
            }
          }
        } catch (error) {
          // Error handling is done globally
        }
      }
    } else {
      // Sequencing is complete, submit with sequenced addresses
      const finalLocationIds = selectedAddresses.map((address: MapMarker) => {
        const sequencedAddress = selectedAddressesForSequencing.find(
          (seqAddress) => seqAddress.id === address.id,
        );

        return {
          locationId: sequencedAddress?.id as string,
          seqNumber: sequencedAddress?.locationSequence as number,
        };
      });

      const targetRegionId =
        moveRegionValues.targetRegionId || values.targetRegionId;

      if (!targetRegionId) {
        setIsSubmitting(false);
        toast.error("Please select a region before moving addresses.");
        return;
      }

      // Close modal immediately before starting async operations
      onClose();
      setIsSubmitting(false);

      try {
        if (allSelectedAddressesUnassigned) {
          addUnassignedLocations({
            pathParams: {
              id: targetRegionId,
            },
            body: {
              locations: finalLocationIds,
              targetSeqNumber: moveRegionValues.targetSeqNumber,
              transferMode: moveRegionValues.transferMode,
            },
          });
        } else {
          // Group addresses by source region
          const addressesByRegion = selectedAddresses.reduce(
            (acc, address) => {
              const regionId = address.region?.id;
              if (regionId) {
                if (!acc[regionId]) {
                  acc[regionId] = [];
                }
                const locationData = finalLocationIds.find(
                  (loc) => loc.locationId === address.id,
                );
                if (locationData) {
                  acc[regionId].push(locationData.locationId);
                }
              }
              return acc;
            },
            {} as Record<string, string[]>,
          );

          // Transfer locations from each source region
          for (const [sourceRegionId, locationIds] of Object.entries(
            addressesByRegion,
          )) {
            transferLocations({
              pathParams: {
                id: sourceRegionId,
              },
              body: {
                targetRegionId: targetRegionId,
                targetSeqNumber: moveRegionValues.targetSeqNumber,
                transferMode: moveRegionValues.transferMode,
                locations: locationIds,
              },
            });
          }
        }
      } catch (error) {
        // Error handling is done globally
      }
    }

    setAllSequenced(false);
  };

  const enabledConfirmButton =
    activeStep === 1
      ? allSequenced
      : values.targetRegionId !== "" &&
        (addressOptions.length > 0
          ? sequence !== undefined && sequence.length > 0
          : true) &&
        !isSubmitting;

  return (
    <Dialog
      onClose={handleClose}
      {...rest}
      sx={
        activeStep === 1 ? styles.dialogWrapperFullWidth : styles.dialogWrapper
      }
      open={open || false}
    >
      <Box sx={styles.dialogTitleWrapper}>
        <DialogTitle variant="titleLarge" sx={styles.dialogTitle}>
          Move Addresses
        </DialogTitle>

        {shouldSequence && (
          <CustomStepper steps={[1, 2]} activeStep={activeStep} />
        )}
      </Box>
      <DialogContent>
        <form>
          {activeStep === 1 ? (
            <RegionSequenceModal />
          ) : (
            fields.map((field) => (
              <Box key={field.key} sx={styles.fieldContainer}>
                <InputLabel sx={styles.inputLabel}>{field.label}</InputLabel>
                <FormControl sx={styles.formControl}>
                  <ControlledSelect
                    defaultValue={""}
                    id={field.key}
                    name={field.key}
                    placeholderSelectEnabled={false}
                    value={
                      field.overrideFormValue
                        ? field.overridenFormValue
                        : values[field.key].toString()
                    }
                    variant="outlined"
                    placeholder={field.placeholder}
                    options={field.options}
                    onChange={(value) => field.onChange(value)}
                    error={touched[field.key] && Boolean(errors[field.key])}
                    disabled={field.disabled || false}
                    MenuProps={{
                      sx: {
                        zIndex: 2147483647,
                      },
                      PaperProps: {
                        sx: {
                          zIndex: 2147483647,
                        },
                      },
                    }}
                  />
                </FormControl>
              </Box>
            ))
          )}
          <DialogActions sx={styles.dialogActions}>
            <Button
              type="button"
              onClick={handleClose}
              sx={styles.cancelButton}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={styles.confirmButton}
              disabled={!enabledConfirmButton}
              data-testid="confirm-button"
              onClick={async () => await submitForm(values)}
            >
              {shouldSequence
                ? activeStep === 1
                  ? "Move Addresses"
                  : "Continue"
                : "Move Addresses"}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeSxStyles(({ palette: { grey, primary } }) => ({
  dialogWrapper: {
    zIndex: 2147483647,
    "& .MuiPaper-root": {
      maxWidth: "900px",
    },
  },
  dialogWrapperFullWidth: {
    zIndex: 2147483647,
    "& .MuiPaper-root": {
      maxWidth: "95vw",
      width: "95vw",
      height: "90vh",
      maxHeight: "90vh",
    },
  },
  dialogTitle: {
    color: grey[100],
    lineHeight: "28px",
  },
  dialogTitleWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  dialogActions: {
    marginTop: "40px",
  },
  cancelButton: {
    color: primary.main,
  },
  confirmButton: {
    backgroundColor: primary.main,
    color: "#00344E",
  },
  fieldContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
  },
  inputLabel: {
    flex: 1,
    paddingRight: "12px",
  },
  formControl: {
    flex: 1,
    width: "210px",
  },
}));

const placementOptions: ControlledLabelSelectOption[] = [
  {
    label: "Before",
    value: "before",
  },
  {
    label: "After",
    value: "after",
  },
];

export default MoveLocationsDialog;
